import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API } from '../App';

const SavingsContext = createContext();

export function SavingsProvider({ children }) {
  const [savingsData, setSavingsData] = useState({
    realizedSavings: 0,
    potentialSavings: 0,
    savedTillNow: 0,
    currentMonthCost: 0,
    totalClusters: 0,
    optimizationRate: null,
    savingsBreakdown: null,
    loading: true
  });

  useEffect(() => {
    // Skip the initial fetch when there's no JWT yet — this provider
    // mounts at app root, so it fires before the user has logged in.
    // Without this guard the call 401s, the global axios interceptor
    // navigates to /login, the page reloads, the provider remounts,
    // and the loop never breaks. The interval still kicks in once a
    // token lands in localStorage.
    if (typeof window !== 'undefined' && localStorage.getItem('token')) {
      fetchSavingsData();
    } else {
      setSavingsData(prev => ({ ...prev, loading: false }));
    }

    // Listen for post-login event so the efficiency score (and the
    // rest of the savings strip) populates immediately after sign-in
    // instead of waiting for the 5-minute interval.
    const onAuth = () => {
      if (typeof window !== 'undefined' && localStorage.getItem('token')) {
        fetchSavingsData();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('optops-auth-changed', onAuth);
    }

    const interval = setInterval(() => {
      if (typeof window !== 'undefined' && localStorage.getItem('token')) {
        fetchSavingsData();
      }
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      if (typeof window !== 'undefined') {
        window.removeEventListener('optops-auth-changed', onAuth);
      }
    };
  }, []);

  const fetchSavingsData = async () => {
    try {
      // Fetch from dashboard stats - single source of truth
      const statsRes = await axios.get(`${API}/dashboard/stats`);
      const stats = statsRes.data;

      setSavingsData({
        realizedSavings: stats.realized_savings || 0,
        potentialSavings: stats.potential_savings || 0,
        savedTillNow: stats.saved_till_now || 0,
        currentMonthCost: stats.current_month_cost || 0,
        totalClusters: stats.total_clusters || 0,
        // null (not 0/100) when there's no idle data or no spend yet — the
        // Efficiency card then shows "—" instead of a score computed on
        // absent data. total_idle_cost is null from the backend when no
        // cluster has an idle timeline.
        optimizationRate: (stats.current_month_cost > 0 && stats.total_idle_cost != null)
          ? Math.round(((stats.current_month_cost - stats.total_idle_cost) / stats.current_month_cost) * 100)
          : null,
        savingsBreakdown: stats.savings_breakdown || null,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch savings data:', error);
      setSavingsData(prev => ({ ...prev, loading: false }));
    }
  };

  const value = {
    ...savingsData,
    refreshSavings: fetchSavingsData
  };

  return (
    <SavingsContext.Provider value={value}>
      {children}
    </SavingsContext.Provider>
  );
}

export function useSavings() {
  const context = useContext(SavingsContext);
  if (!context) {
    throw new Error('useSavings must be used within a SavingsProvider');
  }
  return context;
}

export default SavingsContext;
